import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shared/components/common/Form';
import yup from '@shared/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Input } from '@shared/components/common/Input';
import { Select, SelectGroup, SelectItem, SelectTrigger, SelectValue, SelectContent } from '@shared/components/common/Select';
import { LIST_GENDER_OPTIONS, LIST_WORK_FORM_OPTIONS, PREFECTURES, WORK_TYPE } from '@shared/components/constants/select.constant';
import Calendar from '@shared/components/calendar/Calendar';
import { useEffect, useMemo, useState } from 'react';
import { fetchAddressFromZip } from '@shared/utils/yubinbango';
import { Button } from '@shared/components/common/Button';
import { ROUTES, SCREEN_TYPE } from '@shared/configs/constants';
import { handleBeforeInputNumber, handleInputNumberWithMaxLength, handlePasteNumber, KataNumberReg } from '@shared/utils/common';
import { DATE_YMD_ISO, REGEX_VALIDATE_EMAIL } from '@shared/components/constants/common.constants';
import Heading from '@shared/components/common/Heading';
import { formatDateTime } from '@shared/utils/datetime';
import { useStore } from '@shared/hooks/useStore';
import { toastify } from '@shared/utils/toastify';
import { parse } from 'date-fns';
import { ROLE } from '@/shared/types/enums';
import { cn } from '@/shared/utils/utils';

export default observer(function ProfileEditPage() {
    const navigate = useNavigate();
    const { authStore: { profile, updateUserProfile, getProfile } } = useStore();

    // Form validation schema
    const validateSchema = yup.object().shape({
        firstName: yup.string().trim().required('error.required'),
        lastName: yup.string().trim().required('error.required'),
        firstNameFuri: yup.string().trim().required('error.required').matches(KataNumberReg, 'error.format_hira_kata'),
        lastNameFuri: yup.string().trim().required('error.required').matches(KataNumberReg, 'error.format_hira_kata'),
        companyName: yup.string().trim().required('error.required'),
        companyNameFuri: yup.string().trim().required('error.required').matches(KataNumberReg, 'error.format_hira_kata'),
        customerCode: yup.string().optional().nullable().transform(value => value || ''),
        gender: yup.string().required('error.required'),
        email: yup
            .string()
            .required('error.required')
            .matches(REGEX_VALIDATE_EMAIL, 'form_error_validate.email'),
        confirmEmail: yup
            .string()
            .required('error.required')
            .matches(REGEX_VALIDATE_EMAIL, 'form_error_validate.email')
            .oneOf([yup.ref('email')], 'error.email_confirm_not_match'),
        pharmacistNumber: yup.string().optional().nullable().transform(value => value || ''),
        pharmacistNumberConfirm: yup
            .string()
            .optional()
            .nullable()
            .transform(value => value || '')
            .oneOf(
                [yup.ref('pharmacistNumber'), null],
                'error.student_confirm_pharmacist_not_match_format'
            ),
        phoneNumber: yup.string().required('error.required').typeError('error.number_positive'),
        workForm: yup.string().optional().nullable().transform(value => value || ''),
        zipCode: yup.string().required('error.required').typeError('error.number_positive'),
        prefecture: yup.string().required('error.required'),
        address1: yup.string().required('error.required'),
        address2: yup.string().optional().nullable().transform(value => value || ''),
        workType: yup.string().optional().nullable().transform(value => value || ''),
        birthday: yup.mixed<Date>().required('error.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            firstName: profile?.firstName || '',
            lastName: profile?.lastName || '',
            firstNameFuri: profile?.firstNameFuri || '',
            lastNameFuri: profile?.lastNameFuri || '',
            companyName: profile?.companyName || '',
            companyNameFuri: profile?.companyNameFuri || '',
            customerCode: profile?.customerCode || '',
            gender: profile?.gender?.toString() || '',
            email: profile?.email || '',
            confirmEmail: profile?.email || '',
            pharmacistNumber: profile?.pharmacistNumber || '',
            pharmacistNumberConfirm: profile?.pharmacistNumber || '',
            phoneNumber: profile?.phoneNumber || '',
            workForm: profile?.workForm?.toString() || '',
            zipCode: profile?.zipCode || '',
            prefecture: profile?.prefecture || '',
            address1: profile?.address1 || '',
            address2: profile?.address2 || '',
            workType: profile?.workType || '',
            birthday: profile?.birthday ?
                parse(profile.birthday, 'yyyy-MM-dd', new Date()) :
                undefined
        }
    });

    // Map profile data to form values
    const mapProfileToFormData = (profile: any) => {
        if (!profile) return null;

        const birthdayDate = profile.birthday ?
            parse(profile.birthday, 'yyyy-MM-dd', new Date()) :
            undefined;

        return {
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            firstNameFuri: profile.firstNameFuri || '',
            lastNameFuri: profile.lastNameFuri || '',
            companyName: profile.companyName || '',
            companyNameFuri: profile.companyNameFuri || '',
            customerCode: profile.customerCode || '',
            gender: profile.gender?.toString() || '',
            email: profile.email || '',
            confirmEmail: profile.email || '',
            pharmacistNumber: profile.pharmacistNumber || '',
            pharmacistNumberConfirm: profile.pharmacistNumber || '',
            phoneNumber: profile.phoneNumber || '',
            workForm: profile.workForm?.toString() || '',
            zipCode: profile.zipCode || '',
            prefecture: profile.prefecture || '',
            address1: profile.address1 || '',
            address2: profile.address2 || '',
            workType: profile.workType || '',
            birthday: birthdayDate
        };
    };
    const [isOnlyView, setIsOnlyView] = useState<boolean>(false);

    // Update form when profile data is loaded
    useEffect(() => {
        if (profile) {
            const formData = mapProfileToFormData(profile);
            if (formData) {
                form.reset(formData);
            }
            setIsOnlyView(!!profile.isSynchronizedRecord);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile]);

    const onBlurZipCode = async (e: React.FocusEvent<HTMLInputElement>) => {
        const zip = e.target.value?.replace(/\D/g, '');

        if (zip) {
            const addr = await fetchAddressFromZip(zip);
            if (addr?.region) form.setValue('prefecture', addr?.region);
            if (addr?.locality) form.setValue('address1', `${addr?.locality}${addr?.street}`);
            if (addr?.extended) form.setValue('address2', addr?.extended || '');
        }
    };

    const onSubmit = async (data: FormData) => {
        try {
            const clean = Object.fromEntries(
                Object.entries(data).map(([k, v]) => [k, v === undefined ? null : v])
            );

            const screenType = SCREEN_TYPE.GU;

            // Prepare profile data according to API validation schema
            let profileData: any = {
                screenType,
                firstName: clean.firstName || '',
                lastName: clean.lastName || '',
                firstNameFuri: clean.firstNameFuri || '',
                lastNameFuri: clean.lastNameFuri || '',
                gender: clean.gender || '',
                customerCode: clean.customerCode || '',
                birthday: formatDateTime(data.birthday, DATE_YMD_ISO),
                pharmacistNumber: clean.pharmacistNumber || '',
                confirmPharmacistNumber: clean.pharmacistNumberConfirm || clean.pharmacistNumber || '',
                workForm: clean.workForm || '',
                workType: clean.workType || '',
                zipCode: clean.zipCode || '',
                prefecture: clean.prefecture || '',
                address1: clean.address1 || '',
                address2: clean.address2 || '',
                phoneNumber: clean.phoneNumber || '',
                companyName: clean.companyName || '',
                companyNameFuri: clean.companyNameFuri || '',
                email: clean.email || '',
                confirmEmail: clean.confirmEmail || clean.email || ''
            };

            // if (emailFieldState.isDirty) {
            //     profileData = {
            //         ...profileData,
            //         email: clean.email || '',
            //         confirmEmail: clean.confirmEmail || clean.email || ''
            //     };
            // }

            const result = await updateUserProfile(profileData);

            if (result) {
                await getProfile();
                toastify('success', 'プロフィールが更新されました');
                navigate(ROUTES.profile.href);
            } else {
                toastify('error', 'プロフィールの更新に失敗しました');
            }
        } catch (error) {
            console.error('Failed to save profile:', error);
            toastify('error', 'プロフィールの更新に失敗しました');
        }
    };

    const onError = (error: any) => {
        console.error('Form validation errors:', error);
    };

    const handleCancel = () => {
        navigate(ROUTES.profile.href);
    };

    const isGroupUser = useMemo(() => profile?.roleIds?.includes(ROLE.STD_GROUP), [profile]);

    return (
        <section className='p-6 bg-white rounded-lg md:p-10 animate-in fade-in'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit, onError)} className='flex flex-col gap-4 w-full md:gap-8 sm:gap-5'>
                    {/* 個人情報 */}
                    <div className='bg-background-1 grid sm:grid-cols-2 grid-cols-1 md:gap-6 gap-4 rounded-[4px]'>
                        <div className='col-span-full'>
                            <Heading variant='h4'>個人情報</Heading>
                        </div>

                        <FormField
                            control={form.control}
                            name='firstName'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormLabel className='' required>
                                        姓
                                    </FormLabel>
                                    <FormControl>
                                        <Input className='h-14 rounded-none' {...field} maxLength={255} disabled={isOnlyView} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='lastName'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormLabel className='' required>
                                        名
                                    </FormLabel>
                                    <FormControl>
                                        <Input className='h-14 rounded-none' {...field} maxLength={255} disabled={isOnlyView} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='firstNameFuri'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormLabel className='' required>
                                        姓(フリガナ)
                                    </FormLabel>
                                    <FormControl>
                                        <Input className='h-14 rounded-none' {...field} maxLength={255} disabled={isOnlyView} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='lastNameFuri'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormLabel className='' required>
                                        名(フリガナ)
                                    </FormLabel>
                                    <FormControl>
                                        <Input className='h-14 rounded-none' {...field} autoComplete='new-password' maxLength={255} disabled={isOnlyView} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='gender'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormLabel className='' required>
                                        性別
                                    </FormLabel>
                                    <FormControl>
                                        <Select value={field.value || ''} onValueChange={field.onChange} disabled={isOnlyView}>
                                            <SelectTrigger className='h-14 rounded-none'>
                                                <SelectValue></SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    {LIST_GENDER_OPTIONS.map(gender => (
                                                        <SelectItem key={gender.value} value={gender.value.toString()}>
                                                            {gender.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='birthday'
                            render={({ field, fieldState: { error } }) => (
                                <FormItem className=''>
                                    <FormLabel required>
                                        生年月日
                                    </FormLabel>
                                    <FormControl>
                                        <div className='flex gap-4 items-center w-full'>
                                            <Calendar
                                                classButton={cn('h-14 w-full rounded-none', error && 'border-destructive')}
                                                mode='single'
                                                selected={field.value}
                                                placeholder=''
                                                onSelect={date => field.onChange(date)}
                                                useIconRemove={false}
                                                required
                                                disableButton={isOnlyView}
                                                disabled={[{ after: new Date() }]}
                                            />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='email'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <FormLabel className='' required>
                                        メールアドレス
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            className='h-14 rounded-none'
                                            type='email'
                                            {...field}
                                            maxLength={255}
                                            disabled={isOnlyView}
                                            onChange={(value) => {
                                                field.onChange(value);
                                                if (form.getValues('confirmEmail')?.length > 0) {
                                                    form.trigger('confirmEmail');
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='confirmEmail'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <FormLabel className='' required>
                                        メールアドレスの確認
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            className='h-14 rounded-none'
                                            type='email'
                                            {...field}
                                            maxLength={255}
                                            disabled={isOnlyView}
                                            onChange={(value) => {
                                                field.onChange(value);
                                                form.trigger(['email', 'confirmEmail']);
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='customerCode'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <FormLabel className=''>
                                        お客様番号
                                    </FormLabel>
                                    <FormControl>
                                        <Input className='h-14 rounded-none' {...field} value={field.value || ''} maxLength={255} disabled={isOnlyView} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <hr className='border-t border-border' />

                    {/* 薬剤師情報 */}
                    <div className='bg-background-1 grid sm:grid-cols-2 grid-cols-1 md:gap-6 gap-4 rounded-[4px]'>
                        <div className='col-span-full'>
                            <Heading variant='h4'>薬剤師情報</Heading>
                        </div>

                        <FormField
                            control={form.control}
                            name='pharmacistNumber'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormLabel className=''>
                                        薬剤師名簿登録番号
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            className='h-14 rounded-none'
                                            value={field.value || ''}
                                            maxLength={50}
                                            disabled={isOnlyView}
                                            onPaste={e => e.preventDefault()}
                                            onChange={(value) => {
                                                field.onChange(value);
                                                form.trigger(['pharmacistNumber']);
                                                const confirmValue = form.getValues('pharmacistNumberConfirm');
                                                if (confirmValue && confirmValue.length > 0) {
                                                    form.trigger('pharmacistNumberConfirm');
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='pharmacistNumberConfirm'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormLabel className=''>
                                        薬剤師名簿登録番号確認
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            className='h-14 rounded-none'
                                            value={field.value || ''}
                                            maxLength={50}
                                            disabled={isOnlyView}
                                            onPaste={e => e.preventDefault()}
                                            onChange={(value) => {
                                                field.onChange(value);
                                                form.trigger(['pharmacistNumber', 'pharmacistNumberConfirm']);
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='companyName'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <FormLabel className='' required>
                                        法人名
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            className='h-14 rounded-none'
                                            {...field}
                                            value={field.value || ''}
                                            maxLength={255}
                                            disabled={isOnlyView || isGroupUser}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='companyNameFuri'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <FormLabel className='' required>
                                        法人名（フリガナ）
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            className='h-14 rounded-none'
                                            {...field}
                                            value={field.value || ''}
                                            maxLength={255}
                                            disabled={isOnlyView || isGroupUser}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='workForm'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormLabel className=''>
                                        沖/外選択
                                    </FormLabel>
                                    <FormControl>
                                        <Select value={field.value || ''} onValueChange={field.onChange} disabled={isOnlyView}>
                                            <SelectTrigger className='h-14 rounded-none'>
                                                <SelectValue></SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    {LIST_WORK_FORM_OPTIONS.map(w => (
                                                        <SelectItem key={w.value} value={w.value.toString()}>
                                                            {w.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='workType'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormLabel className=''>
                                        勤務業態
                                    </FormLabel>
                                    <FormControl>
                                        <Select value={field.value || ''} onValueChange={field.onChange} disabled={isOnlyView}>
                                            <SelectTrigger className='h-14 rounded-none'>
                                                <SelectValue></SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    {Object.keys(WORK_TYPE).map(k => (
                                                        <SelectItem key={WORK_TYPE[k]} value={WORK_TYPE[k]}>
                                                            {WORK_TYPE[k]}
                                                        </SelectItem>
                                                    ))}
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <hr className='border-t border-border' />

                    {/* 連絡先情報 */}
                    <div className='bg-background-1 grid sm:grid-cols-2 grid-cols-1 md:gap-6 gap-4 rounded-[4px]'>
                        <div className='col-span-full'>
                            <Heading variant='h4'>連絡先情報</Heading>
                        </div>

                        <FormField
                            control={form.control}
                            name='zipCode'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormLabel className='' required>
                                        郵便番号
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            className='h-14 rounded-none p-postal-code'
                                            {...field}
                                            disabled={isOnlyView}
                                            onInput={e => handleInputNumberWithMaxLength(e, 255)}
                                            maxLength={255}
                                            onBeforeInput={handleBeforeInputNumber}
                                            onPaste={handlePasteNumber}
                                            onBlur={onBlurZipCode}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='prefecture'
                            render={({ field, fieldState: { error } }) => (
                                <FormItem className='relative'>
                                    <FormLabel className='' required>
                                        都道府県
                                    </FormLabel>
                                    <FormControl>
                                        <Select value={field.value} onValueChange={field.onChange} disabled={isOnlyView}>
                                            <SelectTrigger className={cn('h-14 rounded-none', error && 'border-destructive')}>
                                                <SelectValue></SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    {PREFECTURES.map(pref => (
                                                        <SelectItem key={pref} value={pref}>
                                                            {pref}
                                                        </SelectItem>
                                                    ))}
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='address1'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <FormLabel className='' required>
                                        住所
                                    </FormLabel>
                                    <FormControl>
                                        <Input className='h-14 rounded-none' {...field} maxLength={255} disabled={isOnlyView} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='address2'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <FormControl>
                                        <Input className='h-14 rounded-none' {...field} value={field.value || ''} maxLength={255} disabled={isOnlyView} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='phoneNumber'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <FormLabel className='' required>
                                        電話番号
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            className='h-14 rounded-none'
                                            disabled={isOnlyView}
                                            onBeforeInput={handleBeforeInputNumber}
                                            onPaste={handlePasteNumber}
                                            onInput={e => handleInputNumberWithMaxLength(e, 20)}
                                            maxLength={20}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    {/* Action buttons */}
                    <div className='flex gap-4 pt-4 md:gap-8'>
                        <Button
                            type='button'
                            variant='secondary'
                            onClick={handleCancel}
                            className='flex-1 h-10 rounded-lg md:h-14'
                        >
                            {isOnlyView ? '戻る' : 'キャンセル'}
                        </Button>
                        {!isOnlyView && (
                            <Button
                                type='submit'
                                className='flex-1 h-10 rounded-lg md:h-14'
                            >
                                変更する
                            </Button>
                        )}
                    </div>
                </form>
            </Form>
        </section>
    );
});
